import { createSelector } from "@reduxjs/toolkit";
import { createCachedSelector } from 're-reselect';
import { getZoom, getUIProperty, getIsMeter } from "reduxModules/ducks/ui";
import toolIndex from "toolConstants";
import { getStoreProp } from "utilities/string";
import { getRectangle, isInside, getSurface, sortDevicesByColumnThenRow } from "utilities/geometric";
import { findLargest } from "utilities/mathematic";
import { distanceToString, parseDistance, toFeet, toMeter } from "utilities/format";
import devicePlots from "globalConstants/devicePlots";
import { keys, values, omit, set, isEmpty, has, some, get, isString, remove, round, toString, capitalize, ceil } from "lodash";
import { createDeviceAdjutmentAndAim, calculagteCeilingRelativePloptData, calculateWallRelativePlotData } from "reduxModules/calculations/roomCalculations";
import { MIC_CATEGORIES } from "types/models/microphone";
import {
  getActiveTab as getActiveTabFromSelectors,
  getAreaState as getAreaStateFromSelectors,
  getAreaProperty2 as getAreaProperty2FromSelectors,
  getCurrentValue2 as getCurrentValue2FromSelectors,
  getAreaBoxes as getAreaBoxesFromSelectors,
  getAreaGrid as getAreaGridFromSelectors,
  getAreaFineGrid as getAreaFineGridFromSelectors,
  getAreaArray as getAreaArrayFromSelectors,
} from "reduxModules/selectors/areaSelectors";

// Re-export selectors from areaSelectors.ts for backwards compatibility
export const getActiveTab = getActiveTabFromSelectors;

/**
 * Selects the list of area IDs from the state.
 * @param {Object} state - The Redux state.
 * @returns {string[]} An array of area IDs.
 */
export const getAreaList = createSelector(
  state => (state as any).container.areas,
  areas => keys(areas)
);

/**
 * Selects the current area ID if it exists in the area list.
 * @param {Object} state - The Redux state.
 * @returns {string|null} The current area ID or null if not found.
 */
export const getArea = createSelector(getAreaList, getActiveTab, (areas, tab) => areas.includes(tab) ? tab : null);

/**
 * Checks if any area needs recalculation.
 * @param {Object} state - The Redux state.
 * @returns {boolean} True if any area needs recalculation, false otherwise.
 */
export const getRecalculate = state => values((state as any)?.container?.areas ?? {}).some((area: any) => area.recalculate);

/**
 * Checks if any area needs remapping.
 * @param {Object} state - The Redux state.
 * @returns {boolean} True if any area needs remapping, false otherwise.
 */
export const getRemap = state => values((state as any).container?.areas ?? {}).some((area: any) => area.remap);

/**
 * Selects the available area for adding a new area.
 * @param {Object} state - The Redux state.
 * @returns {Object|null} The available area object or null if no space is available.
 *                        The object has the structure:
 *                        { location: { left: number, top: number },
 *                          dimensions: { length: number, width: number } }
 */
export const getAvailableArea = createSelector(
  container => getRectangle({ ...(container as any).dimensions }),
  container => values((container as any).areas).map(area => getRectangle({ ...(area as any).location, ...(area as any).dimensions })),
  container => toolIndex[(container as any).currentTool].constants.area.min,
  (outer, areaList, minDimension) => {
    const coords = outer.concat(areaList).flat();
    const xCords = [...new Set(coords.filter((_, i) => i % 2 === 0))].sort((a: number, b: number) => a - b);
    const yCords = [...new Set(coords.filter((_, i) => i % 2 !== 0))].sort((a: number, b: number) => a - b);
    const matrix = yCords.slice(0, -1).map((y: number, i) => xCords.slice(0, -1).map((x: number, j) => {
      // filter out small areas
      if ((xCords[j + 1] as number) - x <= minDimension || (yCords[i + 1] as number) - y <= minDimension) return 0;
      const testArea = [x, y, xCords[j + 1], yCords[i + 1]] as number[];
      return !some(areaList, (area) => isInside(testArea, area)) ? getSurface(testArea) : 0;
    }));
    const indexes = findLargest(matrix);
    if (some(indexes)) {
      return ({
        location: { left: parseDistance(xCords[indexes[0]] as number), top: parseDistance(yCords[indexes[1]] as number) },
        dimensions: { length: parseDistance((xCords[indexes[2]] as number) - (xCords[indexes[0]] as number)), width: parseDistance((yCords[indexes[3]] as number) - (yCords[indexes[1]] as number)) }
      })
    } else {
      return null
    };
  }
);

// Re-exported from areaSelectors.ts
export const getAreaState = getAreaStateFromSelectors;

/**
 * The folowing three selectors are older implementaion but still used in the code.
 * In all places where the below selectors are used, they hould be replaced with the
 * Three new ones indicated below (same name ending with 2)
 * Any new implementation should use the newer version
 */

/**
 * DO NOT USE - USE getAreaProperty2 below
 * Selects a specific property from an area's state.
 * @param {Object} state - The Redux state.
 * @param {string} property - The property to select from the area state.
 * @returns {*} The value of the specified property from the area state.
 */
export const getAreaProperty = createCachedSelector(
  getAreaState,
  (state, property) => property,
  (areaState, property) => get(areaState, getStoreProp(property))
  // return areaState?.[getStoreProp(property)
)(
  (state, property) => property
);

/**
 * DO NOT USE - USE getContainerProperty2 below
 * Selects a specific property from the container state (excluding areas).
 * @param {Object} state - The Redux state.
 * @param {string} property - The property to select from the container state.
 * @returns {*} The value of the specified property from the container state.
 */
const getContainerProperty = createCachedSelector(
  state => omit((state as any).container, 'areas'),
  (state, property) => property,
  (containerState, property) => get(containerState, getStoreProp(property))
  // containerState?.[getStoreProp(property)]
)(
  (state, property) => property
);

/**
 * DO NOT USE - USE getCurrfentValue2 below
 * Selects the current value of a property, checking area, container, and UI states.
 * @param {Object} state - The Redux state.
 * @param {string} property - The property to select.
 * @returns {*} The current value of the specified property.
 */
export const getCurrentValue = createCachedSelector(
  getAreaProperty,
  getContainerProperty,
  (state, property) => getUIProperty(state, property), // wrapped to avoid TDZ (ui.ts ↔ container.ts cycle)
  (state, property) => property,
  (areaValue, containerValue, uiValue) => areaValue ?? containerValue ?? uiValue ?? null
)(
  (state, property) => property
);

// Re-exported from areaSelectors.ts
export const getAreaProperty2 = getAreaProperty2FromSelectors;

/**
 * REPLACES getContsasinerProperty above
 * Selects a nested property from the container state (excluding areas).
 * @param {Object} state - The Redux state.
 * @param {...string} args - The nested property path to select.
 * @returns {*} The value of the specified nested property from the container state.
 */
const getContainerProperty2 = createCachedSelector(
  state => omit((state as any).container, 'areas'),
  (state, ...args) => isString(args) ? args : remove(args, (x) => x !== undefined).join('.'),
  (containerState, property) => get(containerState, getStoreProp(property))
)(
  (state, ...args) => isString(args) ? args : remove(args, (x) => x !== undefined).join(':')
);

// Re-exported from areaSelectors.ts
export const getCurrentValue2 = getCurrentValue2FromSelectors;

/**
 * Evaluates a condition based on specified parameters and a check function.
 * @param {Object} state - The Redux state.
 * @param {Object} args - The arguments object.
 * @param {string[]} [args.parameter=[]] - The parameters to check.
 * @param {Function} [args.check=() => true] - The check function.
 * @param {string|null} [args.area=null] - The area to check, if applicable.
 * @returns {boolean} The result of the condition check.
 */
export const getConditionResult = (state, args) => {
  const { parameter = [], check = () => true, area = null } = args;

  // Optimize parameter retrieval with better caching
  const parameters = parameter.map(val => {
    // Use getCurrentValue2 for better performance
    return getCurrentValue2(state, val);
  });

  // Cache the result of the check function to avoid repeated expensive calculations
  const result = check([...parameters, area]);
  return result;
};

/**
 * Selects available actions based on specified conditions.
 * @param {Object} state - The Redux state.
 * @param {Object} args - The arguments object.
 * @param {string[]} [args.items=[]] - The list of potential actions.
 * @param {Object} [args.conditions={}] - The conditions for each action.
 * @returns {string[]} The list of available actions.
 */
export const getAvailableActions = createCachedSelector(
  state => state,
  (state, args) => args,
  (state, args) => {
    const { items = [], conditions = {} } = args;
    return items.filter(item => has(conditions, item) ? getConditionResult(state, conditions[item]) : true)
  }
)(
  // getArea
  (state, args) => { return (args as any).area || '0' },
);


// marker these are not eficient yet. When something changes, say name, the whole thing gets recalculated

// fixme: This selector retrieves the entire areas array which contains a lot of data. This should be replaced wherever it;s used by a better selector
/**
 * Gets the areas array.
 * @param {Object} state - The Redux state.
 * @returns {Object|Object[]} An array of all areas.
 */
export const getAreas = state => (state as any).container.areas;

// // fixme: This selector retrieves the entir container (including areas) which contains a lot of data. This should be replaced wherever it;s used by a better selector
/**
 * Selects the current area ID if it exists in the area list.
 * @param {Object} state - The Redux state.
 * @returns {string|null} The current area ID or null if not found.
 */
const getContainer = state => (state as any).container

/**
 * Selects all areas or a specific area based on the active tab.
 * @param {Object} state - The Redux state.
 * @returns {Object|Object[]} Either an array of all areas or a single area object.
 */
// Re-exported from areaSelectors.ts
export const getAreaArray = getAreaArrayFromSelectors;

/**
 * Selects the outer box dimensions based on zoom state and active tab.
 * @param {Object} state - The Redux state.
 * @returns {Object} The outer box dimensions object with length and width properties.
 */
export const getOuterBox = createSelector(
  (state) => getZoom(state), // wrapped to avoid TDZ (ui.ts ↔ container.ts cycle)
  getActiveTab,
  getAreas,
  getContainer,
  (isZoomed, tab, areas, container) => {
    return (isZoomed ? areas[tab] : container).dimensions
  }
);

// Re-exported from areaSelectors.ts
export const getAreaBoxes = getAreaBoxesFromSelectors;

// Re-exported from areaSelectors.ts
export const getAreaGrid = getAreaGridFromSelectors;

// Re-exported from areaSelectors.ts
export const getAreaFineGrid = getAreaFineGridFromSelectors;

/**
 * @function getPlotProps
 * @description Selector that computes plot properties based on the current device, plot type, area data, and units.
 *
 * @type {import('reselect').OutputSelector<RootState, PlotProps, (res1: Area[], res2: string, res3: string, res4: string) => PlotProps>}
 *
 * @param {Area[]} areaArray - Array of areas in the current view
 * @param {string} currentDevice - The currently selected device type
 * @param {string} currentPlot - The currently selected plot type
 * @param {string} currentUnits - The currently selected units (feet or meters)
 *
 * @returns {PlotProps} An object containing computed plot properties
 *
 * @property {number} minLevel - Computed minimum level for the plot
 * @property {number} maxLevel - Computed maximum level for the plot
 * @property {Object} labels - Computed labels for the plot legend
 * @property {string} units - Units for the plot (may be distance units based on currentUnits)
 * @property {number} target - Computed target level (given or weighted average of min and max)
 * @property {number|null} average - Computed average value across all areas, or null if not applicable
 * @property {number|null} deviation - Computed standard deviation, or null if not applicable
 * @property {boolean} error - Indicates if there was an error in computations
 *
 * @throws {Error} Implicitly throws if required data is missing or invalid
 *
 * @example
 * const plotProps = useSelector(getPlotProps);
 * console.log(plotProps.minLevel, plotProps.maxLevel, plotProps.units);
 *
 * @note
 * This selector dynamically computes plot properties based on the current device and plot type.
 * It handles unit conversions and generates appropriate labels for distance-based plots.
 * The selector will return an empty object if the current device or plot is not set.
 */
export const getPlotProps = createCachedSelector(
  getAreaArray,
  (state: any) => state.ui?.currentDevice || null,
  (state: any) => state.ui?.showMap || null,
  state => getCurrentValue2(state, 'units'),
  (areaArray, currentDevice, currentPlot, currentUnits) => {
    // Enhanced safety checks to prevent unset data from causing errors
    if (!currentDevice || currentDevice === null || currentPlot === null) {
      return {};
    }
    if (!devicePlots[currentDevice] || !devicePlots[currentDevice].plots || !devicePlots[currentDevice].plots[currentPlot]) {
      return {};
    }

    const plotProperties = devicePlots?.[currentDevice]?.plots?.[currentPlot];
    if (!plotProperties) {
      return {};
    }
    const { minLevel: min, maxLevel: max, average: avg, deviation: std, roundTo = 1, target: tgt, units: legendUnits, labels: legendLabels } = plotProperties as any;
    const minValues = areaArray.map(obj => get(obj, min));
    const maxValues = areaArray.map(obj => get(obj, max));

    const minLevel = isFinite(min as number) ? min as number : Math.min(...minValues.filter(Boolean));
    const maxLevel = isFinite(max as number) ? max as number : Math.max(...maxValues.filter(Boolean));
    const useDistanceUnits = !legendUnits && !legendLabels;
    let labels = legendLabels;
    let units = legendUnits
    if (useDistanceUnits) {
      units = capitalize(currentUnits)
      const isMeter = currentUnits === 'meters';
      const step = isMeter ? 1 : 3
      const distanceArray = Array.from({ length: Math.ceil(toFeet(maxLevel, isMeter) / step) }, (_, i) => i * step).slice(1);
      labels = distanceArray.reduce((acc, curr) => ({ ...acc, [toString(curr)]: toMeter(curr, isMeter) }), {})
    };
    const target = tgt ? Math.min(...areaArray.map(obj => get(obj, tgt)).filter(Boolean)) : minLevel * .35 + maxLevel * .65;
    let average = areaArray.map(obj => obj[avg]).filter(Boolean).reduce((avg, value, _, { length }) => avg + value / length, 0);
    average = average === 0 ? null : round(average, roundTo);
    let deviation = round(areaArray.map(obj => obj[std]).reduce((avg, value, _, { length }) => avg + value / length, 0), 2);
    deviation = isFinite(deviation) ? deviation : null;
    const error = !isFinite(minLevel) || !isFinite(maxLevel) || !isFinite(average);
    return {
      ...plotProperties,
      minLevel,
      maxLevel,
      labels,
      units,
      target,
      average,
      deviation,
      error
    };
  }
)(
  // Cache key based on stable inputs only
  (state) => {
    const areaArray = getAreaArray(state);
    const currentDevice = getCurrentValue2(state, 'currentDevice');
    const currentPlot = getCurrentValue2(state, 'showMap');
    const currentUnits = getCurrentValue2(state, 'units');

    // Create a stable cache key
    const areaKey = areaArray.map(area => `${area.id}_${area.name}`).join(':');
    return `${currentDevice}_${currentPlot}_${currentUnits}_${areaKey}`;
  }
);

/**
* @function getPlotType
* @description Selects the plot type from plot properties.
* @param {Object} state - Redux state
* @returns {string} The plot type (e.g. 'SPL', 'STI')
*/
const getPlotType = createSelector(getPlotProps, props => (props as any).plotType);

/**
* @function getSource
* @description Selects the data source key from plot properties.
* @param {Object} state - Redux state
* @returns {string} The data source key in the store
*/
const getSource = createSelector(getPlotProps, props => (props as any).source);

/**
* @function getError
* @description Selects the error value from plot properties.
* @param {Object} state - Redux state
* @returns {Object} Error data if present
*/
const getError = createSelector(getPlotProps, props => (props as any).error);

/**
* @function getSPLData
* @description Selects and processes SPL (Sound Pressure Level) data from speaker layouts.
*
* @param {Object} state - Redux state
* @returns {Map} Map containing processed SPL data by area
* @property {string} key - Area ID
* @property {Object} value - Area SPL data
* @property {Array} value.areaData - Array of subdivision and layout data
*
* @description
* Processes speaker layout data by:
* 1. Combining layout data for each area
* 2. Parsing subdivision info from layout IDs
* 3. Mapping receiver and SPL data into a structured format
*
* @example
* const splData = getSPLData(state);
* // Returns Map {
* //   "area1" => {
* //     areaData: [{
* //       subdivision: {left: 0, top: 0, width: 10, length: 10},
* //       data: {
* //         id: "layout1",
* //         receiverSpacing: 0.5,
* //         receiverXOffset: 0,
* //         receiverYOffset: 0,
* //         mapArray: [...]
* //       }
* //     }]
* //   }
* // }
*/
const getSPLData = createCachedSelector(
  getAreaArray,
  getSource,
  (areas, source) => {
    const mergedMap = new Map();
    areas.forEach(area => {
      // Handle warehouse designer case
      if (area.speakerLayout) {
        const combinedArray = area.speakerLayout.map(layout => {
          const id = layout.id;
          const regex = /\s(?<type>[a-z]+)(?<aisleIndex>\d+)/g;
          const { type = null, aisleIndex = null } = regex.exec(id)?.groups || {};
          const subdivision = (type && aisleIndex) ? area.aislesArray[+aisleIndex] : null;
          return {
            subdivision,
            data: {
              id: layout.id,
              receiverSpacing: layout.receiverSpacing,
              receiverXOffset: layout.receiverXOffset,
              receiverYOffset: layout.receiverYOffset,
              mapArray: layout[source]
            }
          };
        });
        mergedMap.set(area.id, { areaData: combinedArray });
      }
      // Handle conference room designer case
      else if (area.speakers?.plotData) {
        if (source && area.speakers.plotData[source]) {
          const plotData = {
            id: `${area.id}-speakers-spl`,
            receiverSpacing: area.speakers.plotData.receiverSpacing || 0.5,
            receiverXOffset: area.speakers.plotData.receiverXOffset || 0,
            receiverYOffset: area.speakers.plotData.receiverYOffset || 0,
            mapArray: area.speakers.plotData[source],
            averageTarget: area.speakers.plotData.totalAwtdSpl || area.speakers.plotData.directAwtdSpl || 73
          };
          mergedMap.set(area.id, { areaData: [{ data: plotData }] });
        }
      }
    });
    return mergedMap;
  }
)(
  state => {
    const areas = getAreaArray(state);
    const plotDataKeys = areas.map(area => {
      const plotData = area.speakers?.plotData;
      const layout = area.speakers?.layout || [];
      const qty = area.speakers?.qty || 0;
      const model = area.speakers?.model || '';

      // Include speaker locations and angles to detect movement
      // Use toFixed(5) for sub-millimeter precision to catch micro-movements
      const layoutKey = layout.map(s =>
        `${s.location?.x?.toFixed(5) || 0},${s.location?.y?.toFixed(5) || 0},${s.horAngle || 0},${s.verAngle || 0},${s.height || 0}`
      ).join(';');

      if (!plotData || !plotData.mapArray) {
        return `${area.id}:none:${model}:${qty}:${layoutKey}`;
      }

      // Include ALL plotData properties that affect rendering
      // This ensures cache invalidates on any change to grid position, averages, or legend bounds
      return [
        area.id,
        plotData.mapArray?.length || 0,
        plotData.receiverSpacing || 0,
        plotData.receiverXOffset || 0,           // Grid X offset
        plotData.receiverYOffset || 0,           // Grid Y offset
        plotData.averageTarget || 0,
        plotData.directAwtdSpl || 0,             // Direct sound average
        plotData.totalAwtdSpl || 0,              // Total sound average
        plotData.stiWithNoiseMasking || 0,       // STI with noise masking
        plotData.directAwtdSplLegendMin || 0,    // Direct SPL legend min
        plotData.directAwtdSplLegendMax || 0,    // Direct SPL legend max
        plotData.totalAwtdSplLegendMin || 0,     // Total SPL legend min
        plotData.totalAwtdSplLegendMax || 0,     // Total SPL legend max
        model,
        qty,
        layoutKey
      ].join(':');
    });
    const finalKey = plotDataKeys.join('|');
    return finalKey;
  }
);

/**
* @function getMicDistanceData
* @description Selects microphone plot data for each area and formats it into a map structure.
*
* @param {Object} state - Redux state
* @returns {Map} Map of microphone plot data by area
* @property {string} key - Area ID
* @property {Object} value - Area plot data
* @property {Array} value.areaData - Array containing plot data object
*
* @description
* This selector:
* 1. Collects areas with microphone plot data
* 2. Creates a map with area IDs as keys
* 3. Formats data into expected structure for visualization
* 4. Skips areas with empty or missing plot data
*
* @example
* const micData = getMicDistanceData(state);
* // Returns Map {
* //   "area1" => {
* //     areaData: [{
* //       data: {plotData from area.microphones}
* //     }]
* //   }
* // }
*
* @note
* Memoization key is based on all area IDs joined with colons,
* ensuring recalculation when areas are added/removed or their order changes.
*/
export const getMicDistanceData = createCachedSelector(
  getAreaArray,
  (state) => (state as any).ui?.deviceIsMoving || false,
  (areas, deviceIsMoving) => {
    // Suppress mic coverage while any device is being dragged so stale data isn't visible mid-move.
    if (deviceIsMoving) return new Map();

    const mergedMap = new Map();
    areas.forEach(area => {
      // Add null safety check for microphones object
      if (!area?.microphones || isEmpty(area.microphones.plotData)) return;
      mergedMap.set(area.id, { areaData: [{ data: area.microphones.plotData }] });
    });
    return mergedMap;
  }
)(
  state => {
    const areas = getAreaArray(state);
    const deviceIsMoving = (state as any).ui?.deviceIsMoving || false;
    const plotDataKeys = areas.map(area => {
      const plotData = area.microphones?.plotData;
      const layout = area.microphones?.layout || [];
      const qty = area.microphones?.qty || 0;
      const model = area.microphones?.model || '';

      // Include microphone locations and heights to detect movement
      // Use toFixed(5) for sub-millimeter precision to catch micro-movements
      const layoutKey = layout.map(m =>
        `${m.location?.x?.toFixed(5) || 0},${m.location?.y?.toFixed(5) || 0},${m.height || 0}`
      ).join(';');

      if (!plotData || !plotData.mapArray) {
        return `${area.id}:none:${model}:${qty}:${layoutKey}`;
      }

      // Include ALL plotData properties that affect rendering
      // This ensures cache invalidates on any change to grid position or microphone configuration
      return [
        area.id,
        plotData.mapArray?.length || 0,
        plotData.receiverSpacing || 0,
        plotData.receiverXOffset || 0,     // Grid X offset
        plotData.receiverYOffset || 0,     // Grid Y offset
        model,
        qty,
        layoutKey
      ].join(':');
    });
    return `${plotDataKeys.join('|')}:${deviceIsMoving}`;
  }
);

/**
* @function getRelativeAverageData
* @description Selects relative average SPL plot data from speaker layouts in each area.
*
* @param {Object} state - Redux state
* @returns {Map} Map of speaker SPL plot data by area
* @property {string} key - Area ID
* @property {Object} value - Area plot data
* @property {Array} value.areaData - Array containing plot data object
*
* @description
* This selector:
* 1. Collects areas that have speaker plot data
* 2. Creates a map using area IDs as keys
* 3. Formats data into the structure required for SPL visualization
* 4. Skips areas with empty or missing plot data
*
* @example
* const splData = getRelativeAverageData(state);
* // Returns Map {
* //   "area1" => {
* //     areaData: [{
* //       data: {plotData from area.speakers}
* //     }]
* //   }
* // }
*
* @note
* - Memoization key combines all area IDs with colons as separator
* - Recalculates when areas are added/removed or their order changes
* - Similar structure to getMicDistanceData but for speaker SPL data
*/
const getRelativeAverageData = createCachedSelector(
  getAreaArray,
  state => toolIndex[(state as any).container.currentTool]?.constants,
  (areas, constants) => {
    const mergedMap = new Map();
    areas.forEach(area => {
      if (!area?.speakers) return;
      const model = area.speakers.model;
      const speakerConfig = constants?.equipment?.speakers?.[model];

      if (speakerConfig?.isCeilingSpkr || speakerConfig?.isPendantSpkr) {
        const layout = area.speakers.layout || [];
        if (layout.length === 0) return;
        const GRID_SPACING = 0.5;
        const { top, left } = area.location;
        const { length, width } = area.dimensions;
        const gridX = ceil((length - left) / GRID_SPACING) + 1;
        const gridY = ceil((width - top) / GRID_SPACING) + 1;
        const gridXOffset = round(((length - left) - (gridX * GRID_SPACING)) / 2, 2);
        const gridYOffset = round(((width - top) - (gridY * GRID_SPACING)) / 2, 2);
        const grid = { gridSpacing: GRID_SPACING, gridXOffset, gridYOffset, gridX, gridY };
        const plotData = calculagteCeilingRelativePloptData(area, layout, grid, constants);
        if (!plotData) return;
        mergedMap.set(area.id, { areaData: [{ data: plotData }] });
      } else if (speakerConfig?.isWallSpkr) {
        const layout = area.speakers.layout || [];
        if (layout.length === 0) return;
        const GRID_SPACING = 0.5;
        const { top, left } = area.location;
        const { length, width } = area.dimensions;
        const gridX = ceil((length - left) / GRID_SPACING) + 1;
        const gridY = ceil((width - top) / GRID_SPACING) + 1;
        const gridXOffset = round(((length - left) - (gridX * GRID_SPACING)) / 2, 2);
        const gridYOffset = round(((width - top) - (gridY * GRID_SPACING)) / 2, 2);
        const grid = { gridSpacing: GRID_SPACING, gridXOffset, gridYOffset, gridX, gridY };
        const plotData = calculateWallRelativePlotData(area, layout, grid, constants);
        if (!plotData) return;
        mergedMap.set(area.id, { areaData: [{ data: plotData }] });
      } else {
        if (isEmpty(area.speakers.plotData)) return;
        mergedMap.set(area.id, { areaData: [{ data: area.speakers.plotData }] });
      }
    });
    return mergedMap;
  }
)(
  state => {
    const areas = getAreaArray(state);
    const plotDataKeys = areas.map(area => {
      const plotData = area.speakers?.plotData;
      const layout = area.speakers?.layout || [];
      const qty = area.speakers?.qty || 0;
      const model = area.speakers?.model || '';

      // Include speaker locations and angles to detect movement
      // Use toFixed(5) for sub-millimeter precision to catch micro-movements
      const layoutKey = layout.map(s =>
        `${s.location?.x?.toFixed(5) || 0},${s.location?.y?.toFixed(5) || 0},${s.horAngle || 0},${s.verAngle || 0},${s.height || 0}`
      ).join(';');

      if (!plotData || !plotData.mapArray) {
        return `${area.id}:none:${model}:${qty}:${layoutKey}`;
      }

      // Include ALL plotData properties that affect rendering
      // This ensures cache invalidates on any change to grid position, averages, or legend bounds
      return [
        area.id,
        plotData.mapArray?.length || 0,
        plotData.receiverSpacing || 0,
        plotData.receiverXOffset || 0,           // Grid X offset
        plotData.receiverYOffset || 0,           // Grid Y offset
        plotData.averageTarget || 0,
        plotData.directAwtdSpl || 0,             // Direct sound average
        plotData.totalAwtdSpl || 0,              // Total sound average
        plotData.stiWithNoiseMasking || 0,       // STI with noise masking
        plotData.directAwtdSplLegendMin || 0,    // Direct SPL legend min
        plotData.directAwtdSplLegendMax || 0,    // Direct SPL legend max
        plotData.totalAwtdSplLegendMin || 0,     // Total SPL legend min
        plotData.totalAwtdSplLegendMax || 0,     // Total SPL legend max
        model,
        qty,
        layoutKey
      ].join(':');
    });
    const finalKey = plotDataKeys.join('|');
    return finalKey;
  }
);

/**
* @function getRelativeAveragePlotData
* @description Retrieves the data from relative average SPL calculations for a specific area.
*
* @param {Object} state - Redux state
* @returns {Array<Array<number>>} 2D array of relative SPL values
*
* @description
* Extracts the processed map array from relative average data by:
* 1. Getting the current area
* 2. Retrieving relative average data for that area
* 3. Accessing the data object from the area's data structure
*
* @example
* const relativeData = getRelativeAveragePlotData(state);
* // Returns {
* //   id: "layout1",
* //   receiverSpacing: 0.5,
* //   receiverXOffset: 0,
* //   receiverYOffset: 0,
* //   mapArray: [...]
* // }
*/
export const getRelativeAveragePlotData = createCachedSelector(
  getArea,
  getRelativeAverageData,
  (area, relativeAreaData) => {
    // Add null safety check for relativeAreaData
    if (!relativeAreaData || relativeAreaData.size === 0) return null;
    const areaData = relativeAreaData.get(area);
    if (!areaData?.areaData?.[0]?.data) return null;
    return areaData.areaData[0].data;
  }
)(
  state => `${getArea(state)}:${getCurrentValue2(state, 'speakers.model') || 'unknown'}`
);

/**
* @function getCameraDistanceData
* @description Selects camera coverage plot data and enhances it with coverage angle information.
*
* @param {Object} state - Redux state
* @returns {Map} Map of camera coverage data by area
* @property {string} key - Area ID
* @property {Object} value - Area plot data
* @property {Array} value.areaData - Array containing plot data object
* @property {Object} value.areaData[0].data.camera - Enhanced camera data including coverage angle
*
* @description
* This selector:
* 1. Collects areas with camera plot data
* 2. Enhances plot data with camera coverage angle
* 3. Creates a map with area IDs as keys
* 4. Skips areas with empty or missing camera data
*
* @example
* const cameraData = getCameraDistanceData(state);
* // Returns Map {
* //   "area1" => {
* //     areaData: [{
* //       data: {
* //         ...plotData,
* //         camera: {
* //           ...cameraData,
* //           coverageAngle: 90
* //         }
* //       }
* //     }]
* //   }
* // }
*
* @note
* - Memoization key combines all area IDs with colons as separator
* - Similar structure to other plot data selectors but includes camera-specific enhancements
* - Uses lodash 'set' to safely update nested camera properties
*/
const getCameraDistanceData = createCachedSelector(
  getAreaArray,
  (state) => (state as any).ui?.deviceIsMoving || false,
  (state) => state,
  (areas, deviceIsMoving, state) => {
    const mergedMap = new Map();
    areas.forEach(area => {
      if (!area?.cameras || isEmpty(area.cameras.plotData)) return;

      // Get cameraConfig from plot data, or fetch from equipment constants if not available
      let cameraConfig = area.cameras.plotData.cameraConfig;
      if (!cameraConfig && area.cameras.model) {
        // Fetch from equipment constants for backwards compatibility with older designs
        const tool = (state as any).container?.currentTool || (state as any).ui?.tool;
        const constants = toolIndex[tool]?.constants;
        cameraConfig = constants?.equipment?.cameras?.[area.cameras.model];
      }

      const isDualImager = cameraConfig?.dualImager === true;

      if (isDualImager) {
        const plotVisibility = area.cameras.plotVisibility || {
          wide_only: true,
          zoom_only: false
        };


        // Create plot data objects - always show both, but render selected one on top
        const baseData = { ...area.cameras.plotData };
        const areaData = [];

        // Calculate opacity based on device movement and selection
        const wideOpacity = deviceIsMoving ? 0 : (plotVisibility.wide_only ? 0.5 : 0.3);
        const zoomOpacity = deviceIsMoving ? 0 : (plotVisibility.zoom_only ? 0.5 : 0.3);

        // Create both wide and zoom plot data
        const wideData = {
          ...baseData,
          id: `${baseData.id}-wide`, // Unique ID for wide-angle plot
          camera: {
            ...baseData.camera,
            coverageAngle: cameraConfig.wideImager.coverageAngle, // 70°
            coverageRadius: cameraConfig.wideImager.coverageRadius, // Fade start
            limitRadius: cameraConfig.wideImager.limitRadius       // 6.096m - Plot end
          },
          opacity: wideOpacity, // Higher opacity when selected, no opcity when moving
          isActive: plotVisibility.wide_only === true // Active state determines gradient color
        };

        const zoomData = {
          ...baseData,
          id: `${baseData.id}-zoom`, // Unique ID for zoom plot
          camera: {
            ...baseData.camera,
            coverageAngle: cameraConfig.zoomImager.coverageAngle, // 45°
            coverageRadius: cameraConfig.zoomImager.coverageRadius, // Fade start
            limitRadius: cameraConfig.zoomImager.limitRadius        // 7.62m - Plot end
          },
          opacity: zoomOpacity, // Higher opacity when selected, no opacity when moving
          isActive: plotVisibility.zoom_only === true // Active state determines gradient color
        };

        // Add plots in order - selected one last (on top)
        if (plotVisibility.wide_only) {
          // Wide selected: zoom on bottom, wide on top
          areaData.push({ data: zoomData });
          areaData.push({ data: wideData });
        } else {
          // Zoom selected: wide on bottom, zoom on top
          areaData.push({ data: wideData });
          areaData.push({ data: zoomData });
        }
        // Add filtered plots to the area data array
        mergedMap.set(area.id, { areaData });
      } else {
        // Standard single camera plot
        const data = {
          ...area.cameras.plotData,
          opacity: deviceIsMoving ? 0 : 0.6 // Hide when moving, otherwise normal opacity
        }
        // Only use config values for camera properties, never fall back to design values
        // This ensures correct values are used when switching between designs
        if (cameraConfig) {
          set(data, 'camera', {
            ...data.camera,
            coverageAngle: cameraConfig.coverageAngle,
            coverageRadius: cameraConfig.coverageRadius,
            limitRadius: cameraConfig.limitRadius
          })
        }
        mergedMap.set(area.id, { areaData: [{ data }] });
      }
    });
    return mergedMap;
  }
)(
  state => `${getAreaArray(state).map(area => area.id).join(':')}:${(state as any).ui?.deviceIsMoving || false}`
);

/**
* @function getMergedData
* @description Combines plot data and area box information based on the current plot type.
*
* @param {Object} state - Redux state
* @returns {Map} Combined map of plot data and area information
* @property {string} key - Area ID
* @property {Object} value - Combined area and plot data
* @property {string} value.id - Area ID with '-heatmap' suffix
* @property {Array} [value.areaData] - Plot data if available
* @property {Object} value.location - Area location information
* @property {Object} value.dimensions - Area dimension information
*
* @description
* This selector:
* 1. Selects appropriate plot data based on plot type:
*    - SPL/STI: Speaker SPL measurements
*    - micDistance: Microphone coverage
*    - relativeAverage: Relative SPL averages
*    - cameraCoverage: Camera coverage
* 2. Merges selected plot data with area box information
* 3. Ensures all areas have entries, even without plot data
*
* @example
* const mergedData = getMergedData(state);
* // For SPL plot type:
* // Returns Map {
* //   "area1" => {
* //     id: "area1-heatmap",
* //     areaData: [{...SPL data}],
* //     location: {left: 0, top: 0},
* //     dimensions: {length: 10, width: 10}
* //   }
* // }
*
* @note
* - Areas without plot data still receive location and dimension information
* - All area IDs are suffixed with '-heatmap' for visualization purposes
* - Existing plot data is preserved when merging with area box information
*/
const getMergedData = createSelector(
  getPlotType,
  getSPLData,
  getMicDistanceData,
  getRelativeAverageData,
  getCameraDistanceData,
  getAreaBoxes,
  (plotType, splData, micDistanceData, relativeAverageData, cameraDistanceData, areaBoxes) => {
    let mergedMap = new Map();
    switch (plotType) {
      case 'SPL':
      case 'STI':
        mergedMap = splData;
        break;
      case 'micDistance':
        mergedMap = micDistanceData;
        break;
      case 'relativeAverage':
        mergedMap = relativeAverageData;
        break;
      case 'cameraCoverage':
        mergedMap = cameraDistanceData
        break;
      default:
    };
    // Merge areaBoxes
    areaBoxes.forEach(box => {
      if (mergedMap.size > 0 && mergedMap.has(box.id)) {
        mergedMap.set(box.id, { ...mergedMap.get(box.id), ...box, id: `${box.id}-heatmap` });
      } else {
        mergedMap.set(box.id, { ...box, id: `${box.id}-heatmap` });
      }
    });
    return mergedMap;
  }
);

/**
* @function getPlotData
* @description Final selector that formats plot data for visualization.
*
* @param {Object} state - Redux state
* @returns {Object} Formatted plot data
* @property {boolean} ready - Whether plot is ready to render
* @property {Array} areaArray - Array of merged area plot data
*
* @description
* Combines plot type, error state, and merged data to:
* 1. Determine if plot is ready to render (has type and no errors)
* 2. Convert merged data map to array format for rendering
*/
export const getPlotData = createSelector(
  getPlotType,
  getError,
  getMergedData,
  (plotType, error, mergedData) => {
    const result = {
      ready: Boolean(plotType) && !error,
      areaArray: Array.from(mergedData.values())
    };


    return result;
  }
);

/**
* @function getListProps
* @description Selects configuration properties for the current equipment list view type.
*
* @param {Object} state - Redux state
* @returns {Object} Configuration for the current list view
* @property {Function} title - Title generator function
* @property {Array<number>} widths - Column width percentages
* @property {string} [deviceType] - Device type for positioning lists
* @property {Array<string>} [notes] - Measurement reference notes
*
* @example
* // For summary view
* const listProps = getListProps(state);
* // Returns {
* //   title: (name, isEmpty) => `${name} equipment list`,
* //   widths: [15, 20, 65]
* // }
*
* @example
* // For positioning detail view
* const listProps = getListProps(state);
* // Returns {
* //   title: (name, isEmpty) => `${name} loudspeaker positioning detail`,
* //   widths: [4, 12, 11, 11, 11, 10, 10, 10, 10, 11],
* //   deviceType: 'speakers',
* //   notes: ['Height is measured from floor', ...]
* // }
*
* @description
* This selector retrieves the configuration for the currently selected list view type.
* It uses the 'showTable' value from the store to determine which configuration
* to return from the equipmentLists constants.
*/
const getListProps = createSelector(
  state => getCurrentValue2(state, 'showTable'),
  state => toolIndex[(state as any).container.currentTool].constants.equipmentLists,
  (listType, lists) => lists[listType]
);

/**
* @function getPositionDetail
* @description Creates a memoized selector that generates detailed positioning data for devices.
*
* @param {Object} state - Redux state
* @returns {Array<Object>} Array of device positioning details
* @property {string} # - Sequential device number
* @property {string} model - Device model name
* @property {string} horizontal\ndist (x) - Horizontal distance from origin
* @property {string} vertical\ndist (y) - Vertical distance from origin
* @property {string} [height\n(z)] - Height from floor (if applicable)
* @property {string} [horizontal\nangle] - Horizontal angle in degrees (if applicable)
* @property {string} [vertical\nangle] - Vertical angle in degrees (if applicable)
* @property {string} [70v Tap] - Power tap setting in watts (if applicable)
* @property {string} [gain] - Gain setting in dB (if applicable)
* @property {string} [delay] - Delay setting in ms (if applicable)
*
* @description
* This selector processes device data by:
* 1. Collecting devices of specified type from all areas
* 2. Applying position adjustments if required (e.g., for wall-mounted devices)
* 3. Formatting measurements according to current unit system (metric/imperial)
* 4. Including device-specific settings where applicable
*
* @example
* // Example return value:
* [
*   {
*     '#': '1',
*     'model': 'Speaker100',
*     'horizontal\ndist (x)': '2.5 m',
*     'vertical\ndist (y)': '3.0 m',
*     'height\n(z)': '2.4 m',
*     'horizontal\nangle': '45 deg',
*     '70v Tap': '30 Watts'
*   }
* ]
*
* @note
* - Properties like height, angles, and device settings are only included if they have valid values
* - Position values are formatted based on the current measurement system (meters/feet)
* - Device position adjustments are applied based on the listProps.adjust flag
* - Memoization is based on the current area to prevent unnecessary recalculations
*/
const getPositionDetail = createCachedSelector(
  getAreaArray,
  (state) => getIsMeter(state), // wrapped to avoid TDZ (ui.ts ↔ container.ts cycle)
  // state => (deviceType, device) => getDeviceAdjustmentAndAim(state, deviceType, device),
  state => {
    const localCalc = toolIndex[(state as any).container.currentTool].constants.tool?.localCalc ?? false
    return localCalc ? createDeviceAdjutmentAndAim(state) : () => { }
  },
  getListProps,
  state => (state as any).container.currentTool,
  (areas, isMeter, deviceAdjustment, listProps, currentTool) => {
    const { deviceType, adjust = false } = listProps;
    // Create device list with area reference to access area-level properties like microphones.height.
    // Each entry carries the speaker model key that governs its row (primary vs expansion) so the
    // per-row config (ceiling/pendant/wall angle columns) resolves against the right model.
    const deviceListWithArea = areas.flatMap(area => {
      const deviceData = area[deviceType];
      const devices = deviceData?.layout ?? deviceData;
      const primary = Array.isArray(devices)
        ? devices.map(device => ({ device, area, speakerModel: area.speakers?.model }))
        : [];

      // Expansion speakers live on a separate `expansionSpeakers` slot and are NOT merged into
      // speakers.layout, so the Speaker location tab must fold them in explicitly.
      // Their mounting height is configured once on the slot (`expansionSpeakers.height`, e.g. the
      // pendant default 8') rather than per placed device, so stamp it onto each row — otherwise
      // the Height column (which reads `device.height`) stays blank for expansion speakers.
      const expansionSpeakers = deviceType === 'speakers' && Array.isArray(area.expansionSpeakers?.layout)
        ? area.expansionSpeakers.layout.map(device => ({
            device: { ...device, height: area.expansionSpeakers?.height ?? device.height },
            area,
            speakerModel: area.expansionSpeakers?.model,
          }))
        : [];

      // Expansion mics are listed from `expansionMicrophones.layout` — the ONE authoritative
      // record of what is actually placed — for BOTH the table and ceiling types.
      //
      // This used to fold in table mics only, and let ceiling mics through via the copy
      // roomCalculations mirrors into `microphones.layout` (allMicCoords). That mirror is not
      // the authoritative layout and routinely disagrees with it on COUNT, so the tab reported
      // the wrong number of expansion mics (PIG: 40x30 + keystone). Two independent causes:
      //   1. The UCC port budget / `maxExpansionMics` trim is applied to
      //      `expansionMicrophones.layout` (setExpansionMicLayout, ducks/room.ts) and to the
      //      heatmap's own input, but NOT to allMicCoords — so an over-budget design mirrored
      //      more mics than it placed.
      //   2. On a view change (including navigating INTO the equipment list) with a path-based
      //      ceiling expansion (round/keystone/vshape), calculateDevicesMiddleware preserves
      //      `expansionMicrophones` against DOM-timing-corrupted re-placement, but the mirror
      //      inside `filteredLayout.microphones` is dispatched anyway — so the mirror took on the
      //      collapsed count while the placed mics kept the good one. Shape- and
      //      acoustics-independent, which is why this reproduced across every table shape and
      //      every acoustic value.
      // Reading the authoritative slot makes this tab immune to both by construction.
      //
      // ORDER: rows are numbered by array index (`'#': index + 1` below), so the listed order has
      // to be spatially deterministic. `expansionMicrophones.layout` is NOT — for ceiling expansion
      // it arrives in #micPath TRAVERSAL order (out along the table's near-to-far edge, around the
      // far end, and back along the opposite edge), and whether the port-cap then regroups it into
      // column order depends on the mic count:
      //   - an EVEN count is mirrored about the table's long axis, so capExpansionGridLayout sees a
      //     uniform grid and re-emits it column-major (x asc, then y asc);
      //   - an ODD count puts one mic alone at the far end, so the layout is NOT a uniform grid and
      //     the cap falls back to order-preserving even-sampling, leaving ring order intact.
      // Since the count is driven by the coverage radius, the numbering convention silently flipped
      // with the acoustic tier — reported as "the top-left expansion mic should be 2" at Fair
      // (odd count → ring order) while Poor (even count → column-major) and Good/Great (single
      // column, where both conventions coincide) looked correct.
      //
      // Sorting here pins every tier to that same x-then-y convention. It also matches the
      // precedent already set for TABLE expansion mics, which `calcExpansionTableMicPlacement`
      // (logic.js) sorts by x then y for exactly this reason ("sort for stable, position-ordered
      // output") — so both expansion types now number identically.
      //
      // `sortDevicesByColumnThenRow` (not a raw `(a.x-b.x) || (a.y-b.y)` comparator): mirrored pairs
      // on a keystone/vshape path sit at the SAME x mathematically, but path sampling leaves a
      // ~1e-12 residue, so a raw x difference is non-zero and decides the order by float noise —
      // the y tie-break never fires and each pair comes out bottom-first. The helper groups columns
      // within a tolerance that also covers the table's own display rounding, so devices showing
      // the same X are always listed top-to-bottom. It returns a new array, which also keeps this
      // off the immer-frozen state layout.
      const expansionMicLayout = sortDevicesByColumnThenRow(
        Array.isArray(area.expansionMicrophones?.layout) ? area.expansionMicrophones.layout : [],
      );
      // `isExpansionMic` tags these rows so the height logic below can resolve their mounting
      // height from the expansion slot instead of `area.microphones` — the two are unrelated.
      // The canonical case is a bar primary (table/wall mounted, FIXED_HEIGHT) with CEILING
      // expansion mics: the primary's category would otherwise suppress the Height (Z) column
      // for the whole table, blanking a height the expansion mics genuinely have and that the
      // installer needs (PIG-585 follow-up).
      const expansionMics = deviceType === 'microphones'
        && (area.expansionMicType === 'table' || area.expansionMicType === 'ceiling')
        ? expansionMicLayout.map(device => ({ device, area, isExpansionMic: true }))
        : [];

      // Drop the mirrored ceiling-expansion copies from the primary rows so they aren't counted
      // twice now that the real ones are folded in above. Same rule (and same legacy `tcm_x`
      // tolerance for designs saved before the dcm_x switch) the canvas already applies in
      // MuiDevices.tsx — expansion mics are rendered from the dedicated slot there too.
      const dedupedPrimary = deviceType === 'microphones' && area.expansionMicType === 'ceiling'
        ? primary.filter(({ device }) => device?.model !== 'dcm_x' && device?.model !== 'tcm_x')
        : primary;

      return [...dedupedPrimary, ...expansionSpeakers, ...expansionMics];
    });

    const tableData = deviceListWithArea.reduce((acc, { device: curr, area, speakerModel, isExpansionMic = false }, index) => {
      // For equipment list, we want actual positions, not adjusted positions
      // Adjustment is only for canvas rendering (moves devices off walls/bar)

      // Determine height value based on device type
      let heightValue;
      if (deviceType === 'speakers') {
        // Most speakers carry their mounting height on `height`, but the bar speaker stores it as
        // location.z instead. Show it either way so the bar row's Height column isn't blank.
        heightValue = isFinite(curr.height) ? curr.height : curr.location?.z;
      } else if (deviceType === 'microphones') {
        // Microphones: check device location.z first, then the group-level height.
        // Expansion mics fall back to `expansionMicrophones.height` (their own configured
        // mounting height — see computeExpansionCeilingMicCoords in roomCalculations.ts) and
        // NOT `microphones.height`, which belongs to a physically unrelated primary mic.
        heightValue = curr.location?.z;
        if (!isFinite(heightValue)) {
          const groupHeight = isExpansionMic
            ? area.expansionMicrophones?.height
            : area[deviceType]?.height;
          if (groupHeight) heightValue = groupHeight;
        }
      } else {
        // Other devices: use location.z
        heightValue = curr.location?.z;
      }

      // Check if this is a bar or table microphone (which have fixed heights)
      // bma360 requires their Z to show — they carry location.z .
      //
      // Resolved PER ROW, not per table: an expansion mic's height is governed by ITS OWN
      // model, not the primary's. A CEILING expansion mic (dcm_x) is pole/flush mounted at a
      // user-configurable height, so it shows Height (Z) even when the primary is a bar mic —
      // which is the usual pairing, since ceiling expansion exists to fill the bar's coverage
      // gap. TABLE expansion (ttm_x) stays suppressed: it's in FIXED_HEIGHT for the same reason
      // a primary tabletop mic is — it sits on the table and the number carries no information.
      let shouldShowHeight = true;
      if (deviceType === 'microphones') {
        if (isExpansionMic) {
          // Keyed off `expansionMicType` rather than `expansionMicrophones.model` so it holds for
          // legacy saves where ceiling expansion was stored as `tcm_x` and for any row whose model
          // didn't survive a round-trip — the type is the user's explicit choice and is always set.
          shouldShowHeight = area.expansionMicType === 'ceiling';
        } else if (area.microphones?.model) {
          shouldShowHeight = !MIC_CATEGORIES.FIXED_HEIGHT.has(area.microphones.model);
        }
      }
      const speakConfig = toolIndex[currentTool]?.constants?.equipment?.speakers?.[speakerModel];
      const isSpeakerPendant = speakConfig?.isPendant || false;
      const isSpeakerCeiling = speakConfig?.isCeiling || false;
      const isSpeakerWall = speakConfig?.isWallSpkr || false;

      // Check if device is a camera
      const isCamera = deviceType === 'cameras';
      const cameraConfig = isCamera ? toolIndex[currentTool]?.constants?.equipment?.cameras?.[area.cameras?.model] : null;
      const isBarCamera = cameraConfig?.isBarCamera ?? false;

      acc.push({
        '#': `${index + 1}`,
        'Model': curr.modelName,
        // Use original device location, not adjusted (adjusted is for canvas rendering only)
        'Horizontal distance (X)': distanceToString(curr.location.x, isMeter),
        'Vertical distance (Y)': distanceToString(curr.location.y, isMeter),
        ...(shouldShowHeight && isFinite(heightValue) && { 'Height (Z)': distanceToString(heightValue, isMeter) }),
        // Show horizontal angle for: cameras (always), ceiling/pendant/wall speakers, and CBC microphones
        ...(isCamera || (isSpeakerCeiling || isSpeakerPendant || isSpeakerWall) || curr.modelName.includes('CBC')) && isFinite(curr.horAngle) && { 'Horizontal angle': `${curr.horAngle} deg` },
        // Show vertical angle for cameras and speakers
        ...((isSpeakerCeiling || isSpeakerPendant || isSpeakerWall)) && isFinite(curr.verAngle) && { 'Vertical angle': `${curr.verAngle} deg` },
        ...isFinite(curr.powerTap70V) && { '70v Tap': `${curr.powerTap70V} Watts` },
        ...isFinite(curr.gain) && { 'gain': `${curr.gain} dB` },
        ...isFinite(curr.delay) && { 'delay': `${curr.delay} ms` }
      });
      return acc
    }, []);
    return tableData
  }
)(
  // Cache key includes area, deviceType, and showTable to invalidate when any of these change
  // This ensures equipment list updates when speaker height or other properties change
  state => `${getArea(state)}:${getCurrentValue2(state, 'showTable')}`
);


/**
* @function getEquipmentSummary
* @description Creates a memoized selector that generates equipment summary tables for multiple areas.
*
* @param {Object} state - Redux state
* @returns {Array<Object>} Array of equipment summary data calculated using tool-specific logic
*
* @description
* This selector:
* 1. Collects all areas from the current state
* 2. Uses tool-specific calculation function from constants
* 3. Generates equipment summary tables
* 4. Memoizes results based on area IDs
*
* @example
* const summary = getEquipmentSummary(state);
* // Returns [
* //   {
* //     item: "Speakers",
* //     quantity: 4,
* //     details: "Ceiling mounted...",
* //     // ... other tool-specific fields
* //   }
* // ]
*
* @note
* - Memoization key is based on concatenated area IDs
* - Calculation logic is determined by the current tool's calculateEquipmentTable function
* - Result structure depends on tool-specific implementation
*/
export const getEquipmentSummary = createCachedSelector(
  getAreaArray,
  state => toolIndex[(state as any).container.currentTool].calculateEquipmentTable,
  (areas, calculateEquipment) => {
    const tableData = calculateEquipment(areas);
    return tableData
  }
)(
  state => {
    const areas = getAreaArray(state);
    return areas.map(area => {
      const micQty = area.microphones?.qty || area.microphones?.layout?.length || 0;
      const spkrQty = area.speakers?.qty || area.speakers?.layout?.length || 0;
      const camQty = area.cameras?.qty || area.cameras?.layout?.length || 0;
      const micModel = area.microphones?.modelName || '';
      const spkrModel = area.speakers?.modelName || '';
      return `${area.id}:${micModel}:${micQty}:${spkrModel}:${spkrQty}:${camQty}`;
    }).join('|');
  }
);

const getAreaEquipmentList = createCachedSelector(
  getAreaArray,
  (areas) => areas[0].equipmentList
)(
  state => `${getAreaArray(state).map(area => area.id).join(':')}`
);

/**
* @function getEquipmentList
* @description Primary selector for generating equipment list data based on current view type.
*
* @param {Object} state - Redux state
* @returns {Object} Complete equipment list configuration and data
* @property {string} title - Generated title for current list view
* @property {Array<Object>} tableData - Data for the equipment table
* @property {Array<number>} [widths] - Column width percentages
* @property {Array<string>} [notes] - Reference notes for measurements
* @property {string} [deviceType] - Type of device for positioning data
*
* @description
* This selector:
* 1. Determines the type of list to display
* 2. Fetches appropriate data using specialized selectors
* 3. Combines list configuration with data
* 4. Handles empty states and missing data
*
* List Types:
* - speakerSummary: Speaker inventory with quantities
* - areaEquipmentList: Comprehensive area equipment list
* - positioningDetail: Detailed device positioning information
*
* @example
* // Area Equipment List
* {
*   title: "Room 101 equipment list",
*   tableData: [{ quantity: 2, item: "Speakers", details: "..." }],
*   widths: [15, 20, 65]
* }
*
* @example
* // Positioning Detail
* {
*   title: "Room 101 loudspeaker positioning detail",
*   tableData: [{ '#': '1', model: 'Speaker100', ... }],
*   widths: [4, 12, 11, ...],
*   notes: ['Height is measured from floor', ...],
*   deviceType: 'speakers'
* }
*
* @note
* - Memoization based on area and selected table view type
* - Empty states handled with appropriate messaging
* - Supports both summary and detailed positioning views
*/
export const getEquipmentList = createCachedSelector(
  state => getCurrentValue2(state, 'name'),
  state => () => getEquipmentSummary(state),
  state => () => getAreaEquipmentList(state),
  state => () => getPositionDetail(state),
  getListProps,
  (currentArea, getEquipmentSummary, getAreaEquipmentList, getPositionDetail, listProps) => {
    const { title = (area) => `${area} does not have equipment data`, list = null, ...props } = listProps;
    let tableData = [];
    switch (list) {
      case 'speakerSummary':
        tableData = getEquipmentSummary();
        break;
      case 'areaEquipmentList':
        tableData = getAreaEquipmentList();
        break;
      case 'positioningDetail':
        tableData = getPositionDetail();
        break;
      default:
    };
    const tableTitle = title(currentArea, isEmpty(tableData));
    return {
      title: tableTitle,
      tableData,
      ...props
    }
  }
)(
  state => `${getArea(state)}:${getCurrentValue2(state, 'showTable')}:${getCurrentValue2(state, 'name')}`
);

/**
* @function getRequestFromData
* @description A Redux selector that generates a formatted list of requested equipment
* from areas, processing both equipment lists and speaker configurations.
*
* @param {Object} state - Redux state
* @returns {Array<string>} Formatted array of equipment requests per area
*
* @description
* Creates equipment request list by:
* 1. Processing each area to find equipment:
*    - Checks for explicit equipment lists
*    - Falls back to speaker configurations
* 2. Formats equipment entries:
*    - Groups by area name
*    - Includes quantities and model names
*    - Maintains consistent formatting
*
* @example
* // State structure
* const state = {
*   container: {
*     areas: {
*       room1: {
*         name: "Conference Room",
*         equipmentList: [
*           { qty: 2, product: { name: "CM20" } }
*         ]
*       },
*       room2: {
*         name: "Training Room",
*         speakers: [...]
*       }
*     }
*   }
* };
*
* // Returns
* [
*   "Conference Room:",
*   "(2)-CM20",
*   "Training Room:",
*   "(4)-DS40"
* ]
*
* @note
* - Uses createSelector for memoization
* - Handles both equipment list and speaker formats
* - Maintains area organization
* - Supports tool-specific equipment calculations
* - Returns consistently formatted strings
*/
export const getRequestFromData = createSelector(
  getAreas,
  state => toolIndex[(state as any).container.currentTool].calculateEquipmentTable,
  (areas, getAreaSpeakers) => {
    const list = [];
    values(areas).forEach(area => {
      if (has(area, 'equipmentList')) {
        list.push(`${(area as any).name}:`);
        (area as any).equipmentList.forEach(item => {
          list.push(`(${item.qty})-${item.product.name}`)
        })
      } else if (has(area, 'speakers')) {
        const speakers = getAreaSpeakers([area]);
        list.push(`${(area as any).name}:`);
        speakers.forEach(item => {
          list.push(`(${item.quantity})-${item.product.name}`)
        })
      };
    })
    return list
  }
);
